import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	computed,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { TransportSegmentsFilters, TransportSegmentsOrdering } from '@/bag/transport-segments/transport-segments.model';

type DateFilterKey = keyof Pick<
	TransportSegmentsFilters,
	'departure_date_before' | 'departure_date_after' | 'return_date_before' | 'return_date_after'
>;

type DateFormControlName = 'departureDateBefore' | 'departureDateAfter' | 'returnDateBefore' | 'returnDateAfter';

type AppliedDateFilters = Pick<
	TransportSegmentsFilters,
	'departure_date_before' | 'departure_date_after' | 'return_date_before' | 'return_date_after'
>;

interface DateFilterChip {
	key: DateFilterKey;
	label: string;
	displayValue: string;
}

interface OrderingOption {
	value: TransportSegmentsOrdering;
	label: string;
}

const DATE_FILTER_CHIP_DEFINITIONS: Array<{
	key: DateFilterKey;
	label: string;
	controlName: DateFormControlName;
}> = [
	{ key: 'departure_date_after', label: 'Wyjazd od', controlName: 'departureDateBefore' },
	{ key: 'departure_date_before', label: 'Wyjazd do', controlName: 'departureDateAfter' },
	{ key: 'return_date_after', label: 'Powrót od', controlName: 'returnDateBefore' },
	{ key: 'return_date_before', label: 'Powrót do', controlName: 'returnDateAfter' },
];

const EMPTY_DATE_FILTERS: AppliedDateFilters = {
	departure_date_before: null,
	departure_date_after: null,
	return_date_before: null,
	return_date_after: null,
};

const DEFAULT_ORDERING: TransportSegmentsOrdering = 'name';

const ORDERING_OPTIONS: OrderingOption[] = [
	{ value: 'name', label: 'Nazwa (A–Z)' },
	{ value: '-name', label: 'Nazwa (Z–A)' },
	{ value: 'date_from', label: 'Data wyjazdu (rosnąco)' },
	{ value: '-date_from', label: 'Data wyjazdu (malejąco)' },
	{ value: 'date_to', label: 'Data powrotu (rosnąco)' },
	{ value: '-date_to', label: 'Data powrotu (malejąco)' },
];

@Component({
	selector: 'app-transport-segments-filters',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatButtonModule,
		MatChipsModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatDatepickerModule,
		MatExpansionModule,
	],
	providers: [{ provide: DateAdapter, useClass: PolishDateAdapter }, DatePipe],
	templateUrl: './transport-segments-filters.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportSegmentsFiltersComponent {
	readonly initialFilters = input<TransportSegmentsFilters | null>(null);
	readonly filtersApplied = output<TransportSegmentsFilters>();
	readonly orderingOptions = ORDERING_OPTIONS;

	readonly filtersForm = new FormGroup({
		search: new FormControl('', { nonNullable: true }),
		ordering: new FormControl<TransportSegmentsOrdering>(DEFAULT_ORDERING, { nonNullable: true }),
		departureDateBefore: new FormControl<Date | null>(null),
		departureDateAfter: new FormControl<Date | null>(null),
		returnDateBefore: new FormControl<Date | null>(null),
		returnDateAfter: new FormControl<Date | null>(null),
	});
	readonly appliedDateFilters = signal<AppliedDateFilters>(EMPTY_DATE_FILTERS);
	readonly dateFilterChips = computed<DateFilterChip[]>(() => {
		const applied = this.appliedDateFilters();
		return DATE_FILTER_CHIP_DEFINITIONS.flatMap(({ key, label }) => {
			const value = applied[key];
			if (!value) {
				return [];
			}
			return [
				{
					key,
					label,
					displayValue: this.formatChipDate(value),
				},
			];
		});
	});
	readonly activeDateFiltersCount = computed(() => this.dateFilterChips().length);

	private readonly destroyRef = inject(DestroyRef);
	private readonly dateAdapter = inject(DateAdapter);
	private readonly datePipe = inject(DatePipe);

	constructor() {
		this.dateAdapter.setLocale('pl-PL');
		effect(
			() => {
				const initialFilters = this.initialFilters();
				if (!initialFilters) {
					return;
				}
				this.filtersForm.patchValue(
					{
						search: initialFilters.search ?? '',
						ordering: initialFilters.ordering ?? DEFAULT_ORDERING,
						departureDateBefore: this.parseDate(initialFilters.departure_date_after),
						departureDateAfter: this.parseDate(initialFilters.departure_date_before),
						returnDateBefore: this.parseDate(initialFilters.return_date_after),
						returnDateAfter: this.parseDate(initialFilters.return_date_before),
					},
					{ emitEvent: false },
				);
				this.appliedDateFilters.set({
					departure_date_before: initialFilters.departure_date_before,
					departure_date_after: initialFilters.departure_date_after,
					return_date_before: initialFilters.return_date_before,
					return_date_after: initialFilters.return_date_after,
				});
			},
			{ allowSignalWrites: true },
		);
		this.filtersForm.controls.search.valueChanges
			.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.emitFilters(this.appliedDateFilters());
			});
		this.filtersForm.controls.ordering.valueChanges
			.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.emitFilters(this.appliedDateFilters());
			});
	}

	applyDateFilters(): void {
		const appliedDateFilters = this.buildAppliedDateFiltersFromForm();
		this.appliedDateFilters.set(appliedDateFilters);
		this.emitFilters(appliedDateFilters);
	}

	clearDateFilters(): void {
		this.filtersForm.patchValue(
			{
				departureDateBefore: null,
				departureDateAfter: null,
				returnDateBefore: null,
				returnDateAfter: null,
			},
			{ emitEvent: false },
		);
		this.appliedDateFilters.set(EMPTY_DATE_FILTERS);
		this.emitFilters(EMPTY_DATE_FILTERS);
	}

	removeDateFilter(key: DateFilterKey): void {
		const definition = DATE_FILTER_CHIP_DEFINITIONS.find((item) => item.key === key);
		if (!definition) {
			return;
		}
		this.filtersForm.controls[definition.controlName].setValue(null, { emitEvent: false });
		const nextFilters = {
			...this.appliedDateFilters(),
			[key]: null,
		};
		this.appliedDateFilters.set(nextFilters);
		this.emitFilters(nextFilters);
	}

	private emitFilters(appliedDateFilters: AppliedDateFilters): void {
		const { search, ordering } = this.filtersForm.getRawValue();
		this.filtersApplied.emit({
			search: this.formatTextFilterValue(search),
			departure_date_before: appliedDateFilters.departure_date_before,
			departure_date_after: appliedDateFilters.departure_date_after,
			return_date_before: appliedDateFilters.return_date_before,
			return_date_after: appliedDateFilters.return_date_after,
			ordering,
		});
	}

	private buildAppliedDateFiltersFromForm(): AppliedDateFilters {
		const { departureDateBefore, departureDateAfter, returnDateBefore, returnDateAfter } =
			this.filtersForm.getRawValue();
		return {
			departure_date_before: this.formatDateFilterValue(departureDateAfter),
			departure_date_after: this.formatDateFilterValue(departureDateBefore),
			return_date_before: this.formatDateFilterValue(returnDateAfter),
			return_date_after: this.formatDateFilterValue(returnDateBefore),
		};
	}

	private formatDateFilterValue(value: Date | null | undefined): string | null {
		if (!value) {
			return null;
		}
		const year = value.getFullYear();
		const month = String(value.getMonth() + 1).padStart(2, '0');
		const day = String(value.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	private formatChipDate(value: string): string {
		return this.datePipe.transform(`${value}T00:00:00`, 'dd.MM.yyyy') ?? value;
	}

	private formatTextFilterValue(value: string | null | undefined): string | null {
		const normalizedValue = value?.trim();
		if (!normalizedValue) {
			return null;
		}
		return normalizedValue;
	}

	private parseDate(value: string | null): Date | null {
		if (!value) {
			return null;
		}
		const parsedDate = new Date(`${value}T00:00:00`);
		if (Number.isNaN(parsedDate.getTime())) {
			return null;
		}
		return parsedDate;
	}
}
